import stableStringify from "fast-json-stable-stringify";
import { Value } from "@sinclair/typebox/value";
import { z } from "zod/v4";
import {
  BASE_RECORD_SURVIVORSHIP_TYPES,
  SOURCE_SURVIVORSHIP_TYPES,
  SURVIVORSHIP_TYPES,
  SurvivorshipTypes,
} from "@hightouch/lib/idr/golden-record/constants";
import {
  IdrCreateGraphScheduleSchema,
  type IdrCreateGraphSchedule,
} from "@hightouch/lib/idr/create-schedule";
import {
  ProbabilisticConfigSchema,
  type ProbabilisticConfig,
} from "@hightouch/lib/idr/v2/probabilistic/types";

export const IDR_GRAPH_DOCUMENT_KIND = "hightouch.idr.graph";
export const IDR_GRAPH_DOCUMENT_SCHEMA_VERSION = 2;

const SlugSchema = z.string().min(1);
const ColumnNameSchema = z.string().min(1);
const IdentifierSchema = z.string().min(1);

export const IdrGraphDocumentMergeRuleSchema = z
  .object({
    identifier: IdentifierSchema,
  })
  .strict();

export const IdrGraphDocumentLimitRuleSchema = z
  .object({
    identifier: IdentifierSchema,
    limit: z.number().int().positive(),
  })
  .strict();

const OrderBySchema = z
  .object({
    order: z.enum(["asc", "desc"]),
    column: ColumnNameSchema,
  })
  .strict();

const MappingPairSchema = z
  .object({
    column: ColumnNameSchema,
    identifier: IdentifierSchema,
  })
  .strict();

function mappingRecordFromPairs(
  pairs: ReadonlyArray<{ column: string; identifier: string }>,
): Record<string, string> {
  return Object.fromEntries(
    pairs.map(({ column, identifier }) => [column, identifier]),
  );
}

// Zod's z.record assigns onto {}, which drops reserved names such as __proto__.
const ModelMappingsSchema: z.ZodType<Record<string, string>> = z.preprocess(
  (value) => {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }
    return Object.entries(value).map(([column, identifier]) => ({
      column,
      identifier,
    }));
  },
  z.array(MappingPairSchema).transform(mappingRecordFromPairs),
);

const ModelBaseSchema = z.object({
  model: SlugSchema,
  rank: z.number().int(),
  mappings: ModelMappingsSchema,
  watermarkLookbackDays: z.number().int().nonnegative().optional(),
  scopeColumn: z.string().min(1).optional(),
  goldenRecordBaseSurvivorship: z
    .enum(BASE_RECORD_SURVIVORSHIP_TYPES)
    .optional(),
});

export const IdrGraphDocumentModelSchema = z.discriminatedUnion("type", [
  ModelBaseSchema.extend({
    type: z.literal("profile"),
    orderBy: OrderBySchema.optional(),
  }).strict(),
  ModelBaseSchema.extend({
    type: z.literal("event"),
    orderBy: OrderBySchema,
  }).strict(),
]);

export type IdrGraphDocumentModel = z.infer<typeof IdrGraphDocumentModelSchema>;

export const IdrGraphDocumentSourcePrioritySchema = z
  .object({
    model: SlugSchema,
    survivorship: z.enum(SOURCE_SURVIVORSHIP_TYPES),
  })
  .strict();

const GoldenRecordColumnBaseSchema = z.object({
  name: ColumnNameSchema,
});

export const IdrGraphDocumentGoldenRecordColumnSchema = z.union([
  GoldenRecordColumnBaseSchema.extend({
    identifier: IdentifierSchema,
    survivorship: z.enum(SURVIVORSHIP_TYPES),
    sourcePriority: z.array(IdrGraphDocumentSourcePrioritySchema).optional(),
  }).strict(),
  GoldenRecordColumnBaseSchema.extend({
    alias: ColumnNameSchema,
    survivorship: z
      .enum(SURVIVORSHIP_TYPES)
      .exclude([SurvivorshipTypes.SOURCE_PRIORITY]),
  }).strict(),
]);

export type IdrGraphDocumentGoldenRecordColumn = z.infer<
  typeof IdrGraphDocumentGoldenRecordColumnSchema
>;

const GoldenRecordBaseColumnsSchema = z
  .object({
    model: SlugSchema,
    columns: z.array(ColumnNameSchema),
  })
  .strict();

export const IdrGraphDocumentGoldenRecordSchema = z
  .object({
    enabled: z.boolean(),
    columns: z.array(IdrGraphDocumentGoldenRecordColumnSchema),
    baseColumns: z.array(GoldenRecordBaseColumnsSchema),
  })
  .strict();

export type IdrGraphDocumentGoldenRecord = z.infer<
  typeof IdrGraphDocumentGoldenRecordSchema
>;

const ProbabilisticSchema = z.custom<ProbabilisticConfig>((value) => {
  if (!Value.Check(ProbabilisticConfigSchema, value)) {
    return false;
  }
  const cleaned = Value.Clean(
    ProbabilisticConfigSchema,
    structuredClone(value),
  );
  return stableStringify(value) === stableStringify(cleaned);
}, "Invalid probabilistic config");

const IdrGraphDocumentScheduleSchema = z.custom<IdrCreateGraphSchedule>(
  (value) => {
    const parsed = IdrCreateGraphScheduleSchema.safeParse(value);
    return (
      parsed.success && stableStringify(value) === stableStringify(parsed.data)
    );
  },
  "Invalid IDR schedule",
);

export const IdrGraphDocumentBodySchema = z
  .object({
    name: z.string().min(1),
    description: z.string().optional(),
    version: z.literal(2),
    source: SlugSchema,
    output: z
      .object({
        table: z.string().min(1),
        schema: z.string().min(1).optional(),
      })
      .strict(),
    schedule: IdrGraphDocumentScheduleSchema,
    schedulePaused: z.boolean(),
    mergeRules: z.array(IdrGraphDocumentMergeRuleSchema),
    limitRules: z.array(IdrGraphDocumentLimitRuleSchema),
    probabilistic: ProbabilisticSchema,
    onCompleteSql: z.string().min(1).optional(),
    models: z.array(IdrGraphDocumentModelSchema),
    goldenRecord: IdrGraphDocumentGoldenRecordSchema.optional(),
  })
  .strict();

export type IdrGraphDocumentBody = z.infer<typeof IdrGraphDocumentBodySchema>;

export const IdrGraphDocumentSchema = z
  .object({
    kind: z.literal(IDR_GRAPH_DOCUMENT_KIND),
    schemaVersion: z.literal(IDR_GRAPH_DOCUMENT_SCHEMA_VERSION),
    ...IdrGraphDocumentBodySchema.shape,
  })
  .strict();

export type IdrGraphDocument = z.infer<typeof IdrGraphDocumentSchema>;

export type IdrGraphDocumentBodyKey = keyof IdrGraphDocumentBody;
